import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { useSelector, useDispatch } from "react-redux";
import { fetchRepairsAsync } from "../../../../../../store/Slice/repairSlice";
import type { RootState, AppDispatch } from "../../../../../../store/store";

const RepairDonutChart: React.FC = () => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { items } = useSelector((state: RootState) => state.repairs);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await dispatch(fetchRepairsAsync({ page: 1, limit: 1000 }) as any);
      setLoading(false);
    };
    load();
  }, [dispatch]);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    if (chartInstance.current) chartInstance.current.destroy();

    // ---- New Logic: Count Approved (Complete) ----
    const counts = items.reduce(
      (acc, item) => {
        const type = item.requestType === "repair" ? "Repair" : "Replacement";
        acc[type] = (acc[type] || 0) + 1;

        // Pending / Delayed
        const today = new Date();
        if (!item.expectedDeliveryDate || new Date(item.expectedDeliveryDate) >= today) {
          acc.Pending += 1;
        } else {
          acc.Delayed += 1;
        }

        // APPROVED → Complete
        if (item.isApproved === true) {
          acc.Complete += 1;
        }

        return acc;
      },
      {
        Repair: 0,
        Replacement: 0,
        Pending: 0,
        Delayed: 0,
        Complete: 0, // new field
      }
    );

    const data = {
      labels: ["Repair", "Replacement", "Pending", "Delayed", "Complete"],
      datasets: [
        {
          data: [
            counts.Repair,
            counts.Replacement,
            counts.Pending,
            counts.Delayed,
            counts.Complete,
          ],
          
          backgroundColor: [
            "hsl(217 91% 60%)", 
            "hsl(262 83% 58%)", 
            "hsl(43 96% 56%)", 
            "hsl(33 100% 50%)", 
            "hsl(142 76% 36%)", 
          ],
          borderWidth: 0,
        },
      ],
    };

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) return;

    chartInstance.current = new Chart(ctx, {
      type: "doughnut",
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { usePointStyle: true },
          },
        },
        cutout: "70%",
      },
    });

    return () => chartInstance.current?.destroy();
  }, [items, loading]);

  return (
    <div className="w-full">
      <h3 className="text-start font-semibold">Repair Status Distribution</h3>
      <div
        style={{
          width: "300px",
          height: "300px",
          margin: "0 auto",
        }}
      >
        {loading ? <p>Loading chart data...</p> : <canvas ref={chartRef} />}
      </div>
    </div>
  );
};

export default RepairDonutChart;
